/**
 * Hotjar Service
 *
 * Provides integration with Hotjar for user behaviour tracking
 * and heatmap analysis on key pages in the Zwizle application.
 */

// Type definitions for Hotjar window object
declare global {
  interface Window {
    hj: ((
      command: 'event' | 'identify' | 'stateChange' | 'init',
      eventName?: string,
      properties?: Record<string, string | number | boolean>
    ) => void) & {
      q?: Array<unknown>
    }
    _hjSettings: {
      hjid: number
      hjsv: number
    }
  }
}

// Hotjar configuration - these would come from environment variables in production
const HOTJAR_ID = (import.meta.env.VITE_HOTJAR_ID as unknown as number) || 0
const HOTJAR_SNIPPET_VERSION =
  (import.meta.env.VITE_HOTJAR_SNIPPET_VERSION as unknown as number) || 6

/**
 * Initialize Hotjar tracking
 * This should be called once at application startup
 */
export const initHotjar = (): void => {
  if (!HOTJAR_ID) {
    console.warn('Hotjar ID not found. Hotjar will not be initialized.')
    return
  }

  try {
    // Skip initialization if Hotjar is already loaded
    if (typeof window.hj === 'function' && window._hjSettings?.hjid) {
      console.info('Hotjar already initialized.')
      return
    }

    // Hotjar tracking code
    ;(function (h: Window, o: Document, t: string, j: string) {
      h.hj =
        h.hj ||
        function (
          ...args: Array<
            string | Record<string, string | number | boolean> | undefined
          >
        ) {
          ;(h.hj.q = h.hj.q || []).push(args)
        }
      h._hjSettings = { hjid: HOTJAR_ID, hjsv: HOTJAR_SNIPPET_VERSION }
      const a = o.getElementsByTagName('head')[0]
      const r = o.createElement('script')
      r.async = true
      r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv
      a.appendChild(r)
    })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=')
  } catch (error) {
    console.error('Failed to initialize Hotjar:', error)
  }
}

/**
 * Track a virtual page view in Hotjar
 * Call this whenever a route changes to track page views
 *
 * @param pageUrl - The current page URL
 */
export const trackPageView = (pageUrl: string): void => {
  if (!window.hj) {
    console.warn('Hotjar not initialized. Page view not tracked:', pageUrl)
    return
  }

  try {
    window.hj('stateChange', pageUrl)
  } catch (error) {
    console.error('Failed to track Hotjar page view:', error)
  }
}

/**
 * Track a custom event in Hotjar
 * Use this to track specific user interactions or events
 *
 * @param eventName - The name of the event to track
 * @param properties - Optional properties to associate with the event
 */
export const trackEvent = (
  eventName: string,
  properties?: Record<string, string | number | boolean>
): void => {
  if (!window.hj) {
    console.warn('Hotjar not initialized. Event not tracked:', eventName)
    return
  }

  try {
    window.hj('event', eventName, properties)
  } catch (error) {
    console.error('Failed to track Hotjar event:', error)
  }
}

export default {
  initHotjar,
  trackPageView,
  trackEvent,
}
